import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import "../styles/SKUDashboard.css";
import axios from 'axios';
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GridLoader } from 'react-spinners';
import { skuReportPdf } from './skuReportPdf';
import { subscribe } from "./eventBus";

const QR_TYPES = [
  { key: "NR", label: "Data QR" },
  { key: "DR", label: "Data QRmor" },
  { key: "TR", label: "Non-Clonable 12 mm QRmor" },
  { key: "FR", label: "Non-Clonable 14 mm QRmor" },
  { key: "ER", label: "Non-Clonable 18 mm QRmor" },
];

const QR_TYPE_TO_BUCKET = {
  normal: "NR",
  normal_qrmor: "DR",
  T: "TR",
  F: "FR",
  E: "ER",
};

const QR_TYPE_LABELS = {
  normal: { label: "Data QR", value: "normal" },
  normal_qrmor: { label: "Data QRmor", value: "normal_qrmor" },
  T: { label: "Non-Clonable 12 mm QRmor", value: "1.2" },
  F: { label: "Non-Clonable 14 mm QRmor", value: "1.4" },
  E: { label: "Non-Clonable 18 mm QRmor", value: "1.8" },
};

function SKUDashboard() {
  const navigate = useNavigate();

  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [qrDataMap, setQrDataMap] = useState({ NR: [], TR: [], FR: [], ER: [], DR: [] });
  const [filters, setFilters] = useState({ NR: "", TR: "", FR: "", ER: "", DR: "" });
  const [pages, setPages] = useState({ NR: 1, TR: 1, FR: 1, ER: 1, DR: 1 });

  const [sku, setSku] = useState({});
  const [skuImage, setSkuImage] = useState(null);
  const [qrCount, setQrCount] = useState({});
  const [isAdmin, setisAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showLicenseAlert, setShowLicenseAlert] = useState(false);
  const [noBatchAlert, setNoBatchAlert] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [skuData, setSkuData] = useState([]);
  const [showPopupModal, setShowPopupModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState('all');
  const [qrType, setQrType] = useState('');
  const qrTypeError = useRef("");

  const baseURL = sessionStorage.getItem('baseURL');

  const activeTypes = QR_TYPES.filter(({ key }) => qrDataMap[key]?.length > 0);

  const getFilteredData = (key) =>
    (qrDataMap[key] || []).filter((row) =>
      String(row.uid).toLowerCase().includes(filters[key].toLowerCase())
    );

  const getCurrentRows = (key) => {
    const filtered = getFilteredData(key);
    return filtered.slice((pages[key] - 1) * rowsPerPage, pages[key] * rowsPerPage);
  };

  const setFilter = (key, val) => setFilters((p) => ({ ...p, [key]: val }));
  const prevPage = (key) => setPages((p) => ({ ...p, [key]: Math.max(1, p[key] - 1) }));
  const nextPage = (key) => {
    const total = Math.ceil(getFilteredData(key).length / rowsPerPage);
    setPages((p) => ({ ...p, [key]: Math.min(total, p[key] + 1) }));
  };

  /* ── Resize + notifications ── */
  useEffect(() => {
    const calculateRows = () => {
      const rows = Math.floor((window.innerHeight - 280 - 60 - 50 - 80) / 63);
      setRowsPerPage(rows >= 2 ? rows : 2);
    };
    calculateRows();

    if (sessionStorage.getItem('isAdmin') !== "true") {
      fetchNotification();
      const unsub = subscribe((e) => { if (e.type === "NOTIFICATION") fetchNotification(); });
      return unsub;
    }

    window.addEventListener("resize", calculateRows);
    return () => window.removeEventListener("resize", calculateRows);
  }, []);

  const fetchNotification = () =>
    axios.get(baseURL + "notifications/unread", {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    }).then(res => setNotifications(res.data));

  useEffect(() => {
    if (notifications.length === 0) setShowNotificationModal(false);
  }, [notifications]);
  useEffect(() => {
    console.log(sku)
  }, [sku])

  const fetchImageAsDataURL = async (imageUrl) => {
    const res = await axios.get(imageUrl, { responseType: "arraybuffer" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const fetchDetails = async () => {
    try {
      document.getElementById("loading").hidden = false;
      let skuTemp = JSON.parse(sessionStorage.getItem("skuId"));
      if (typeof skuTemp.qr_types === "string") {
        skuTemp.qr_types = JSON.parse(skuTemp.qr_types);
        sessionStorage.setItem("skuId", JSON.stringify(skuTemp))
        setSku(skuTemp)
      }

      setSku(skuTemp)
      if (!skuTemp) return;

      const imageUrl = baseURL + "sku/SKUImage/" + skuTemp.product_image.replaceAll("/", "-");
      const [groupResp, qrCountResp, qrResp, skuResp, dataURL] = await Promise.all([
        axios.get(`${baseURL}pu/getGroupIds/${skuTemp.prod_unit}`),
        axios.get(`${baseURL}qr/qrcount/${skuTemp.sku_id}`),
        axios.get(`${baseURL}qr/fetchQR/${skuTemp.sku_id}`),
        axios.get(`${baseURL}sku/fetchSku/all`),
        fetchImageAsDataURL(imageUrl),
      ]);

      const updatedSku = { ...skuTemp, address: groupResp.data?.[0]?.address || "" };
      setSku(updatedSku);
      sessionStorage.setItem("skuId", JSON.stringify(updatedSku));
      setSkuImage(dataURL);
      setQrCount(qrCountResp.data);

      const buckets = { NR: [], TR: [], FR: [], ER: [], DR: [] };
      qrResp.data.forEach((qr) => {
        const match = Object.keys(buckets).find((k) => qr.qr_id.includes(k));
        if (match) buckets[match].push(qr);
      });
      setQrDataMap(buckets);
      setPages((p) => {
        const next = { ...p };
        Object.keys(buckets).forEach((k) => { if (buckets[k].length > 0) next[k] = 1; });
        return next;
      });
      setSkuData(skuResp.data);
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      document.getElementById("loading").hidden = true;
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') !== "true") { navigate('/'); return; }
    if (sessionStorage.getItem('isAdmin') === "true") setisAdmin(true);
    fetchDetails();
  }, [navigate]);

  /* ── Actions ── */
  const generateReport = (type) => {
    if (type === "detailed") setShowPopupModal(true);
    else skuReportPdf(sku, false);
  };

  const triggerGeneration = () => {
    skuReportPdf(sku, true, selectedQR === 'all' ? null : selectedQR);
    setShowGenerateModal(false);
    setShowPopupModal(false);
    setSelectedQR('all');
  };

  function showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.innerText = message;
    Object.assign(toast.style, {
      position: "fixed", top: "20px", right: "20px",
      background: type === "error" ? "#C0392B" : type === "warning" ? "#e67e22" : "#1a6b3c",
      color: "#fff", padding: "12px 20px", borderRadius: "8px",
      boxShadow: "0 6px 20px rgba(0,0,0,0.18)", zIndex: 9999, opacity: "0",
      fontSize: "0.92rem", fontFamily: "'Source Sans 3', system-ui, sans-serif",
      fontWeight: "600", transition: "opacity 0.3s ease",
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = "1"));
    setTimeout(() => { toast.style.opacity = "0"; setTimeout(() => toast.remove(), 300); }, 3500);
  }

  const generateQR_start = () => {
    if (qrType === '') { qrTypeError.current = "Please select a QR type."; return; }
    qrTypeError.current = "";
    document.getElementById('loading').hidden = false;
    setShowGenerateModal(false);
    const newQrData = new FormData();
    newQrData.append("qrId",
      qrType === 'normal' ? (qrCount['NR'] + 1) + 'NR' :
        qrType === 'normal_qrmor' ? (qrCount['DR'] + 1) + "DR" :
          qrType === '1.2' ? (qrCount['TR'] + 1) + "TR" :
            qrType === '1.4' ? (qrCount['FR'] + 1) + "FR" :
              (qrCount['ER'] + 1) + "ER"
    );
    newQrData.append("skuId", sku.sku_id);
    newQrData.append("gtin", sku.gtin);
    newQrData.append("genericName", sku.generic_name);
    newQrData.append("brand", sku.brand_name);
    newQrData.append("license", sku.manufacturing_license);
    newQrData.append("sku_id_ref", sku.id);
    newQrData.append("qrType", qrType);

    axios.post(baseURL + 'qr/generateQR', newQrData, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
    })
      .then((response) => {
        if (response.data.error) { showToast("Error: " + response.data.error, "error"); return; }
        showToast("QRmor Code Generated Successfully");
        document.getElementById('loading').hidden = true;
        fetchDetails();
      })
      .catch((error) => {
        if (error.response?.data?.error === "License Expired") setShowLicenseAlert(true);
        else showToast("Error generating QR Code. Please try again.", "error");
        document.getElementById('loading').hidden = true;
      });
  };

  const route = (qrId, uid, createdAt, batch_count) => {
    if (isAdmin && batch_count === 0) { setNoBatchAlert(true); return; }
    sessionStorage.setItem('qrId', qrId);
    sessionStorage.setItem('qrUid', uid);
    sessionStorage.setItem('qrCreatedAt', createdAt);
    navigate('/batch-management');
  };

  const downloadQR = (qrId, uid, pdf) => {
    axios.get(baseURL + "qr/fetchQRDoc/" + qrId + "." + pdf + "/" + sku.sku_id, { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', uid + '.' + pdf);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
  };

  const handleNotificationClick = (notification) => {
    const filtered_sku = skuData.find(item => item.sku_id === notification.payload.sku_id);
    if (!filtered_sku?.disabled) {
      sessionStorage.setItem('skuId', JSON.stringify(filtered_sku));
      axios.get(baseURL + "qr/fetchSingleQR/" + filtered_sku.sku_id + "/" + notification.payload.qr_id)
        .then((response) => {
          const d = response.data[0];
          sessionStorage.setItem('qrId', d.qr_id);
          sessionStorage.setItem('qrUid', d.uid);
          sessionStorage.setItem('qrCreatedAt', d.timestamp);
          navigate('/batch-management');
        });
    }
  };

  const changeNotificationStatus = (notification) =>
    axios.get(baseURL + 'notifications/changeNotificationStatus/' + notification.id)
      .then(() => fetchNotification())
      .catch((err) => console.error("Error changing notification status:", err));

  const batchLabel = isAdmin ? "View Batch Details" : "Click to Add & Validate Batch Details";

  /* ── Render ── */
  return (
    <>
      <div className="skuDashboard">

        {/* ════════════════════ NAV HEADER ════════════════════ */}
        <div className="dashboard-header">
          <div className="dashboard-actions">
            <button className="action-button" onClick={() => navigate('/dashboard')}>
              ← Back
            </button>
          </div>

          {!isAdmin && (
            <div className="notification-icon">
              <button
                className="action-button"
                onClick={() => notifications.length > 0 && setShowNotificationModal(true)}
                style={{ cursor: notifications.length > 0 ? 'pointer' : 'default' }}
              >
                <FontAwesomeIcon icon={faBell} />
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <span className="notification-count">{notifications.length}</span>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ════════════════════ STICKY ACTION BAR ════════════════════
            All primary actions are always visible here.
        ════════════════════════════════════════════════════════════ */}
        <div className="action-bar">
          {/* Product name — gives context */}
          <span className="ab-title">
            {sku.brand_name || "Product Dashboard"}
          </span>

          <div className="ab-actions">
            {/* Admin-only primary actions */}
            {isAdmin && (
              <>
                <button
                  className="ab-btn-primary"
                  onClick={() => setShowGenerateModal(true)}
                  title="Generate a new QRmor code for this product"
                >
                  + Generate QR
                </button>

                <button
                  className="ab-btn-secondary"
                  onClick={() => {
                    sessionStorage.setItem("recallBatch", JSON.stringify(sku));
                    navigate('/recall-batch');
                  }}
                  title="Recall a batch associated with this product"
                >
                  Recall Batch
                </button>

                <div className="ab-divider" />
              </>
            )}

            {/* Available to all roles */}
            <button
              className="ab-btn-ghost"
              onClick={() => setShowModal(true)}
              title="View or print a product report"
            >
              ⬇ Product Report
            </button>
          </div>
        </div>

        {/* ════════════════════ MAIN CONTENT ════════════════════ */}
        <div className="skuDashboard-container">

          {/* ── Left: product info card (info only, no actions) ── */}
          <div className="left-section">
            <div className="info-card">
              {/* Product image at top */}
              {skuImage && (
                <div className="product-image-wrap">
                  <img src={skuImage} alt={sku.brand_name || "Product"} />
                </div>
              )}

              {/* Details table */}
              <table>
                <tbody>
                  <tr>
                    <td>Product Name</td>
                    <td>{sku.brand_name}</td>
                  </tr>
                  <tr>
                    <td>GTIN</td>
                    <td>{sku.gtin}</td>
                  </tr>
                  <tr>
                    <td>Generic Name</td>
                    <td>{sku.generic_name}</td>
                  </tr>
                  <tr>
                    <td>Manufacturing Lic. No.</td>
                    <td>{sku.manufacturing_license}</td>
                  </tr>
                  {isAdmin && <>
                    <tr>
                      <td>Production Unit</td>
                      <td>{sku.prod_unit}<br />{sku.address}</td>
                    </tr>
                  </>}
                  <tr>
                    <td>List of Excipients</td>
                    <td>{sku.excipients}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Right: QR columns ── */}
          <div className="right-section">
            <p className="qr-section-title">QRmor Codes</p>

            <div
              className="contents"
              style={{
                gridTemplateColumns: `repeat(${Math.max(activeTypes.length, 1)}, 1fr)`,
              }}
            >
              {activeTypes.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  background: 'var(--c-white)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 12,
                  padding: '48px 24px',
                  textAlign: 'center',
                  color: 'var(--c-muted)',
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>No QRmor codes yet</div>
                  {isAdmin && (
                    <div style={{ fontSize: '0.88rem' }}>
                      Use <strong>+ Generate QR</strong> in the toolbar above to create one.
                    </div>
                  )}
                </div>
              ) : (
                activeTypes.map(({ key, label }) => {
                  const filteredData = getFilteredData(key);
                  const currentRows = getCurrentRows(key);
                  const currentPage = pages[key];
                  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;

                  return (
                    <div key={key} className="batch-filter-container">

                      {/* Column header with count */}
                      <div className="qr-col-header">
                        <span className="qr-col-title">{label}</span>
                        <span className="qr-col-count">
                          {filteredData.length} QR codes{filteredData.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Search */}
                      <div className="qr-col-search">
                        <input
                          type="text"
                          placeholder={`Search UID…`}
                          value={filters[key]}
                          onChange={(e) => setFilter(key, e.target.value)}
                        />
                      </div>

                      {/* Table */}
                      <div className="batch-table">
                        <div className="table-container">
                          <table>
                            <thead>
                              <tr>
                                <th>QR UID</th>
                                {isAdmin && <th>File</th>}
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {currentRows.length === 0 ? (
                                <tr>
                                  <td colSpan={isAdmin ? 3 : 2} style={{ textAlign: 'center', color: 'var(--c-muted)', fontStyle: 'italic' }}>
                                    No results
                                  </td>
                                </tr>
                              ) : currentRows.map((row) => (
                                <tr key={row.id}>
                                  <td className="uid-cell">{row.uid}</td>
                                  {isAdmin && (
                                    <td>
                                      <button
                                        className="download-button"
                                        onClick={() => downloadQR(row.qr_id, row.uid, "cdr")}
                                        title="Download CDR file"
                                      >
                                        CDR
                                      </button>
                                    </td>
                                  )}
                                  <td>
                                    <button
                                      className="row-action-btn"
                                      onClick={() => route(row.qr_id, row.uid, row.timestamp, row.batch_count)}
                                    >
                                      {batchLabel} →
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="pagination">
                          <button onClick={() => prevPage(key)} disabled={currentPage <= 1}>← Prev</button>
                          <span>{currentPage} / {totalPages}</span>
                          <button onClick={() => nextPage(key)} disabled={currentPage >= totalPages}>Next →</button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Loading ── */}
        <div className="loading" id="loading" hidden>
          <GridLoader color="var(--c-navy)" loading={true} size={18} />
        </div>

        {/* ════ Generate QR modal ════ */}
        {showGenerateModal && (
          <div className="verify-modal">
            <div className="verify-content">
              <h3>Generate QRmor Code</h3>
              <p>Select the QR type for <strong>{sku.brand_name}</strong>.</p>
              <select
                className="qr-type-select"
                value={qrType}
                onChange={(e) => setQrType(e.target.value)}
              >
                <option value="">Select QR Type…</option>
                {Object.entries(typeof sku.qr_types == 'string'? JSON.parse(sku.qr_types): sku.qr_types || {})
                  .filter(([_, val]) => val.checked)
                  .map(([key]) => (
                    <option key={key} value={QR_TYPE_LABELS[key].value}>
                      {QR_TYPE_LABELS[key].label}
                    </option>
                  ))}
              </select>
              {qrTypeError.current !== '' && (
                <p className="error-message">{qrTypeError.current}</p>
              )}
              <div className="verify-actions">
                <button
                  className="verify-button"
                  onClick={() => { setShowGenerateModal(false); setQrType(''); }}
                  style={{ background: 'var(--c-danger-pale)', color: 'var(--c-danger)', border: '1px solid rgba(192,57,43,0.22)' }}
                >
                  Cancel
                </button>
                <button className="verify-button" onClick={generateQR_start}>
                  Generate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ Report type modal ════ */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-box">
              <h3>Select Report Type</h3>
              <p>Choose the format for your product report.</p>
              <div className="modal-actions">
                <button className="btn simple" onClick={() => { generateReport("simple"); setShowModal(false); }}>Simple Report</button>
                <button className="btn detailed" onClick={() => { generateReport("detailed"); setShowModal(false); }}>Detailed Report</button>
              </div>
              <button className="cancel" onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* ════ License expired alert ════ */}
        {showLicenseAlert && (
          <div className="license-overlay">
            <div className="license-popup">
              <h4>License Expired</h4>
              <p>Your license for this product has expired. Please renew it to continue generating QR codes.</p>
              <button onClick={() => setShowLicenseAlert(false)}>Close</button>
            </div>
          </div>
        )}

        {/* ════ No-batch alert ════ */}
        {noBatchAlert && (
          <div className="no-batch-overlay">
            <div className="no-batch-popup">
              <h4>No Batch Details Found</h4>
              <p>There are no batch details available for this QRmor Code yet.</p>
              <button onClick={() => setNoBatchAlert(false)}>Close</button>
            </div>
          </div>
        )}
      </div>

      {/* ════ Notifications panel (dropdown from top-right) ════ */}
      {showNotificationModal && (
        <div className="notification-modal">
          <h3>Notifications</h3>
          <div className="notification-content">
            <table>
              <tbody>
                {notifications.map((notification, index) => (
                  <tr key={index} className="notification-item">
                    <td>
                      <strong>{notification.type}</strong><br />
                      <span style={{ fontSize: '0.8rem', color: 'var(--c-muted)' }}>
                        {notification.payload.batch_no} · {notification.payload.uid} · {notification.payload.sku_name}
                      </span>
                    </td>
                    <td>
                      {["Batch Added", "Batch Requested for Update", "Batch Updated"].includes(notification.type)
                        ? <button onClick={() => handleNotificationClick(notification)}>View</button>
                        : <button onClick={() => changeNotificationStatus(notification)}>OK</button>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="notification-button" onClick={() => setShowNotificationModal(false)}>
            Close
          </button>
        </div>
      )}

      {/* ════ Detailed report popup ════ */}
      {showPopupModal && (
        <div className="popup-modal">
          <div className="popup-content">
            <h3>Detailed Report</h3>
            <div className="popup-filter">
              <div className="popup-filter-item">
                <label>Filter by QR ID</label>
                <select onChange={(e) => setSelectedQR(e.target.value === "all" ? "all" : JSON.parse(e.target.value))}>
                  <option value="all">All QR Codes</option>
                  {Object.entries(sku.qr_types || {})
                    .filter(([_, val]) => val.checked)
                    .map(([key]) => {
                      const bucketKey = QR_TYPE_TO_BUCKET[key];
                      const rows = qrDataMap[bucketKey] || [];
                      if (rows.length === 0) return null;
                      return (
                        <optgroup key={bucketKey} label={QR_TYPE_LABELS[key].label}>
                          {rows.map((row, index) => (
                            <option key={`${bucketKey}-${index}`} value={JSON.stringify(row)}>
                              {row.uid}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                </select>
              </div>
            </div>
            <div className="popup-buttons">
              <button className="popup-button can" onClick={() => setShowPopupModal(false)}>Cancel</button>
              <button className="popup-button gen" onClick={triggerGeneration}>Generate Report</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SKUDashboard;